/**
 * The command palette's decisions. **Pure**, so every case is checkable without
 * a DOM - same split as `header-view.ts`, `panel-view.ts` and `thread-view.ts`.
 *
 * ┌─ ⛔ ACTIONS RANK ABOVE RECORDS, AND IT IS A TWO-TIER SORT ─────────────────┐
 * │ `Build_Order:485`: *"ACTIONS RANK ABOVE RECORDS when the query matches      │
 * │ one."* That is **not** one scored list. A contact scoring 0.83 must still   │
 * │ rank below an action scoring 0.5, and a palette that merges both into one   │
 * │ score is wrong at any set size.                                            │
 * │                                                                            │
 * │ ⚠️ WITH TWO ACTIONS IT CANNOT FAIL OBSERVABLY, WHICH IS WHY THE TEST FOR   │
 * │ IT WILL READ AS OVER-TESTING. INBOX ships `snooze` and `assign`; `tag` and │
 * │ `add task` are CRM-BUILD's, having no table, column or producer. No contact │
 * │ is named "snooze", so every real query lands in ONE tier and the between-   │
 * │ tier comparison is never reached - **a merged-score implementation would   │
 * │ pass every test written from real data.**                                  │
 * │                                                                            │
 * │ ⛔ THE INVERTED CASE - A RECORD SCORING HIGHER THAN AN ACTION MUST STILL   │
 * │ RANK BELOW IT - IS THE ONLY ONE THAT SEPARATES THE TWO IMPLEMENTATIONS, and │
 * │ it cannot arise from real data. It is constructed in the test. **The test  │
 * │ carries the property until the action set grows enough to exercise it       │
 * │ naturally, and deleting it as redundant later is exactly how it would be   │
 * │ lost.**                                                                    │
 * └────────────────────────────────────────────────────────────────────────────┘
 */
import type { ContactHit } from '../../../src/services/contact-search.js'

/**
 * ⛔ SAFE MUTATIONS ONLY, AND THE LIST IS SHORT BECAUSE TWO OF THE FOUR HAVE NO
 * STORAGE. `Build_Order:484` names *"snooze, assign, tag, add task. NOTHING
 * DESTRUCTIVE, and nothing that sends a message to a customer."* `tag` and
 * `add task` have no table, no column and no producer anywhere - both are
 * CRM-BUILD's, and the palette gains them the day those tables exist without
 * this union changing shape.
 *
 * ⚠️ `close` IS ABSENT AND ITS ABSENCE IS THE RULE, NOT AN OMISSION: it writes a
 * visible artefact into the customer-facing narrative, which is why it confirms
 * in the thread header and is excluded here. **The palette is fast and
 * low-confirmation by design, which is exactly why its action set must be
 * recoverable.**
 */
export type PaletteAction =
  | { kind: 'snooze' }
  | { kind: 'assign' }

/** Everything the palette can offer, in the order it offers it. */
export type PaletteRow =
  | { tier: 'action'; action: PaletteAction; score: number }
  | { tier: 'record'; contact: ContactHit; score: number }

const ACTION_WORDS: ReadonlyArray<{ action: PaletteAction; words: readonly string[] }> = [
  { action: { kind: 'snooze' }, words: ['snooze', 'remind', 'later'] },
  { action: { kind: 'assign' }, words: ['assign', 'owner', 'assignee'] },
]

/**
 * How well a query matches an action, 0 when it does not.
 *
 * ⚠️ PREFIX MATCHING, NOT FUZZY. An action is a command somebody is typing on
 * purpose, and a trigram match would offer "assign" for "design". The contact
 * side is fuzzy because a NAME is something you half-remember; a verb is not.
 */
export function actionScore(query: string, action: PaletteAction): number {
  const q = query.trim().toLowerCase()
  if (q === '') return 0
  const entry = ACTION_WORDS.find((a) => a.action.kind === action.kind)
  let best = 0
  for (const w of entry?.words ?? []) {
    if (!w.startsWith(q)) continue
    // A full word scores 1; a prefix scores by how much of it was typed, so
    // "assi" ranks below "assign" without either being excluded.
    best = Math.max(best, q.length / w.length)
  }
  return best
}

/**
 * ⛔ THE TWO-TIER SORT. Actions first, ordered among themselves; then records,
 * ordered among themselves. **The two scores are never compared with each
 * other**, which is the whole rule.
 */
export function rankPalette(query: string, contacts: readonly ContactHit[]): PaletteRow[] {
  const actions: PaletteRow[] = ACTION_WORDS.map((a) => ({
    tier: 'action' as const,
    action: a.action,
    score: actionScore(query, a.action),
  }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)

  const records: PaletteRow[] = contacts
    .map((c) => ({ tier: 'record' as const, contact: c, score: Number(c.score) }))
    /**
     * ⚠️ NOT RE-SORTED BY SCORE ALONE. The service already ordered these, with a
     * name tie-break that exists because identical names score identically -
     * re-sorting here on `score` would discard that and make the order of equal
     * hits whatever `Array.prototype.sort` does with them.
     */
    .slice()

  return [...actions, ...records]
}

/**
 * What the palette shows when it has nothing to show, which is three different
 * sentences and not one.
 *
 * ⛔ SAME DISTINCTION THE PANEL AND THE THREAD BOTH HAD TO MAKE. "You have not
 * typed enough", "we are looking" and "there is nothing" are different facts,
 * and rendering the third for the first tells somebody their colleague does not
 * exist because they typed two letters.
 */
export type PaletteEmpty =
  | { kind: 'tooShort' }
  | { kind: 'searching' }
  | { kind: 'noMatches' }
  | { kind: 'hasRows' }

export function paletteEmpty(params: {
  query: string
  minQuery: number
  loading: boolean
  rows: readonly PaletteRow[]
}): PaletteEmpty {
  if (params.rows.length > 0) return { kind: 'hasRows' }
  /**
   * ⚠️ THE ORDER OF THESE TESTS IS THE DECISION. A short query is short whether
   * or not a request is in flight, and reporting "searching" for a two-letter
   * query would promise an answer that is never coming - the contact route
   * returns an empty list for anything below `MIN_QUERY` rather than searching.
   */
  if (params.query.trim().length < params.minQuery) return { kind: 'tooShort' }
  if (params.loading) return { kind: 'searching' }
  /**
   * ⚠️ `noMatches` IS TRUE ABOUT CONTACTS AND READS AS A CLAIM ABOUT THE INBOX.
   * The palette never searches message bodies - by design - and says nothing to
   * that effect, so somebody typing a phrase they remember from a conversation
   * is told "nothing matches". Amir's inspection finding 18, deferred; the
   * remedy is a fourth state handing off to `/search`, which DOES search bodies.
   * Registered as `palette-cannot-search-messages-and-does-not-say-so`.
   */
  return { kind: 'noMatches' }
}

/**
 * The shortcut, written the way this platform writes it.
 *
 * ⛔ ADDED 2026-08-20 WITH THE PALETTE'S FIRST VISIBLE ENTRY POINT. Amir's
 * inspection: *"No visible entry point to the command palette - Ctrl+K only,
 * which nobody discovers."*
 *
 * ⚠️ **THE HANDLER ACCEPTS BOTH KEYS AND ALWAYS DID** - `InboxShell` tests
 * `e.ctrlKey || e.metaKey` - so this is about which one to SHOW. Printing
 * "Ctrl+K" to a Mac user names a key that does work but is not the one anyone
 * there would reach for, and printing both makes a hint longer than the label.
 *
 * ⚠️ AND IT TAKES THE PLATFORM STRING AS A PARAMETER rather than reading
 * `navigator`, so both branches are reachable in a test. A function that reads a
 * global directly has one branch per test environment, which is how the untested
 * branch ships.
 */
export function shortcutHintFor(platform: string): string {
  return /mac|iphone|ipad/i.test(platform) ? '⌘K' : 'Ctrl+K'
}
